import { View } from 'react-native';
import React from 'react';
import { APITypes } from '../../../primary-record-types';
import * as Card from '../../v2BaseComponents/Card';
import getDateString from '../../../utils/getDateString';
import _truncate from 'lodash/truncate';
import { EntityReferences } from '../../../primary-record-types/apiTypes/sharedTypes';
import getNoteTags from '../Notes/utils/getNoteTags';
import getNoteLinkState from '../Notes/utils/getNoteLinkState';
import NoteLinkPill from '../Notes/NoteDetails/NoteLinkPill';
import useNotes from '../../../hooks/api/useNotes';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Divider from '../../v2BaseComponents/Divider';
import { LoadableAvatar, getFallbackFromName } from '../../v2BaseComponents/Avatar';
import urls from '../../../utils/urls';
import getName from '../../../utils/getName';
import SelectableMinimalCard from '../SelectableMinimalCard';
import MarkdownDisplay from '../../v2BaseComponents/MarkdownDisplay';

export type NotSelectableProps = {
    isSelectable: false | undefined;
    onPress?: (note: APITypes.notesService.NoteListItem | APITypes.notesService.NoteDetails) => void;
};

export type SelectableProps = {
    isSelectable: true;
    isSelected: boolean;
    onSelect: () => void;
    isDisabled?: boolean;
    testID?: string;
    flat?: boolean;
};

export type SelectableNoteCardProps = {
    note: APITypes.notesService.NoteListItem | APITypes.notesService.NoteDetails;
} & SelectableProps;

export type NoteCardOwnProps = {
    note: APITypes.notesService.NoteListItem | APITypes.notesService.NoteDetails;
    onPress?: (note: APITypes.notesService.NoteListItem | APITypes.notesService.NoteDetails) => void;
    flat?: boolean;
};

export type NoteCardProps = NoteCardOwnProps;

// Stable opening of the AI-summary disclaimer prepended to system-generated clinical notes. Hardcoded here
// (keep in sync with Notes.AI_SUMMARY_DISCLAIMER_LEAD_IN in primary-record-types, which the note-service
// generator builds from) because the app's copied types directory has no committed index.ts to import the
// value from at runtime — a value import of that path breaks the Metro bundle.
const AI_SUMMARY_DISCLAIMER_LEAD_IN = 'The following summary was generated by AI';

// System-generated clinical notes prepend an AI-summary disclaimer paragraph to their body. In the list
// preview that text is identical for every note, so skip it here (the full note view still shows it). Detect
// via the disclaimer lead-in and drop that first paragraph.
const getNotePreviewMarkdown = (
    note: APITypes.notesService.NoteListItem | APITypes.notesService.NoteDetails,
): string => {
    const markdown = note.markdownContent ?? '';
    if (!note.isSystemGenerated) return markdown;
    const separatorIndex = markdown.indexOf('\n\n');
    const firstParagraph = separatorIndex === -1 ? markdown : markdown.slice(0, separatorIndex);
    if (!firstParagraph.includes(AI_SUMMARY_DISCLAIMER_LEAD_IN)) return markdown;
    return separatorIndex === -1 ? '' : markdown.slice(separatorIndex + 2).trimStart();
};

const BaseNoteCard: React.FC<NoteCardProps> = (props) => {
    const getReferenceCounts = (): Partial<EntityReferences> => ({
        comment: props.note.comments.length,
        attachments: props.note.images.length + props.note.files.length,
    });

    const lastComment = props.note.comments[props.note.comments.length - 1];
    // A copied comment on a retained client-record copy resolves no account; it carries its author's name instead.
    const lastCommentAuthor = lastComment
        ? lastComment.createdBy
            ? getName(lastComment.createdBy)
            : lastComment.createdBySourceLabel ?? null
        : null;
    const linkState = getNoteLinkState(props.note);

    return (
        <>
            <Card.CardPadding>
                <Card.CardContent>
                    <Stack horizontal fullWidth justify="space-between" align="flex-start" space={2}>
                        <View style={{ flex: 1 }}>
                            <Card.CardHeader>{props.note.title}</Card.CardHeader>
                        </View>
                        {linkState ? <NoteLinkPill state={linkState} testID={`note-link-pill-${linkState}`} /> : null}
                    </Stack>
                    <Card.CardBody2>
                        {getDateString(props.note.contentDate ?? props.note.createdAt, 'date-time')}
                    </Card.CardBody2>
                    <MarkdownDisplay truncateLength={40}>{getNotePreviewMarkdown(props.note)}</MarkdownDisplay>
                    <Stack horizontal fullWidth align="center" justify="space-between">
                        <Stack horizontal flex={7}>
                            <Card.CardChipSet chips={getNoteTags(props.note)} style={{ flex: 1 }} />
                        </Stack>
                        <Stack horizontal flex={3} justify="flex-end">
                            <Card.CardReferenceCounts reference={getReferenceCounts()} />
                        </Stack>
                    </Stack>
                </Card.CardContent>
            </Card.CardPadding>
            {lastComment ? (
                <>
                    <Divider horizontal />
                    <Card.CardPadding>
                        <Stack vertical fullWidth space={contentStackSpacing} align="flex-start">
                            <Stack horizontal fullWidth justify="flex-start" space={contentStackSpacing}>
                                <LoadableAvatar
                                    avatarUrl={
                                        lastComment.createdBy
                                            ? urls.api.notes.noteUserAvatar(props.note.noteId, lastComment.createUserId)
                                            : null
                                    }
                                    fallbackName={lastCommentAuthor ? getFallbackFromName(lastCommentAuthor) : ''}
                                    size="sm"
                                />
                                <Stack vertical justify="center" align="flex-start">
                                    <Card.CardBody>{lastCommentAuthor}</Card.CardBody>
                                    <Card.CardBody2>{getDateString(lastComment.createdAt, 'date-time')}</Card.CardBody2>
                                </Stack>
                            </Stack>
                            <Card.CardBody>{lastComment.comment}</Card.CardBody>
                        </Stack>
                    </Card.CardPadding>
                </>
            ) : null}
        </>
    );
};

export const SelectableNoteCard: React.FC<SelectableNoteCardProps> = ({
    isSelectable,
    isSelected,
    onSelect,
    isDisabled,
    note,
    flat,
    testID,
}) => {
    return (
        <SelectableMinimalCard
            isSelectable={isSelectable}
            isSelected={isSelected}
            onSelect={onSelect}
            isDisabled={isDisabled}
            disablePadding
            style={{ paddingLeft: Card.defaultCardPadding.paddingLeft }}
            flat={flat}
            testID={testID}
        >
            <BaseNoteCard note={note} />
        </SelectableMinimalCard>
    );
};

const NoteCard: React.FC<NoteCardProps> = (props) => {
    const handleCardPress = () => {
        props.onPress?.(props.note);
    };

    return (
        <Card.Card onPress={handleCardPress} testID="note-card" disablePadding flat={props.flat}>
            <BaseNoteCard note={props.note} onPress={props.onPress} />
        </Card.Card>
    );
};

export default React.memo(NoteCard);

export type UnloadedNoteCardProps = Omit<NoteCardProps, 'note'> & {
    noteId: string;
    skeletonFallback?: boolean;
};

export const UnloadedNoteCard: React.FC<UnloadedNoteCardProps> = ({ noteId, skeletonFallback, ...rest }) => {
    const { note, isLoadingNote } = useNotes({
        noteId,
    });

    if (!note || isLoadingNote) return skeletonFallback ? <Card.CardSkeleton /> : null;

    return <NoteCard note={note} {...(rest as any)} />;
};
